/** feat-338 — the page-wide filter bar. Filters only; Export CSV sits on the Questions header (Q5). */
import React from 'react';
import type { LearnLang, QuestionType } from '../../../../services/questionAnalysisService';
import { C, QA_COPY, TYPE_LABEL, LANG_LABEL } from './questionAnalysisFormat';
import type { QaFilterState } from './useQaFilters';

export interface Option {
  id: string;
  name: string;
}

interface Props {
  filters: QaFilterState;
  locations: Option[];
  drills: Option[];
  onChange: (patch: Partial<QaFilterState>) => void;
  onReset: () => void;
}

const EARLIER_ID = 'qa-include-earlier';

const FilterBar: React.FC<Props> = ({ filters, locations, drills, onChange, onReset }) => {
  const on = !!filters.include_earlier_versions;
  return (
    <div className="bg-white rounded-2xl border border-softBorder shadow-sm p-3 md:p-4 mb-5" role="group" aria-label={QA_COPY.filters}>
      <div className="flex flex-wrap items-end gap-3">
        <div className={C.label}>
          {QA_COPY.dates}
          <div className="flex flex-wrap items-center gap-1.5">
            <input
              type="date"
              data-testid="filter-start"
              aria-label={QA_COPY.from}
              value={filters.start ?? ''}
              max={filters.end || undefined}
              onChange={(e) => onChange({ start: e.target.value || undefined })}
              className={`${C.select} w-[150px]`}
            />
            <span className="text-inkMuted text-sm">{QA_COPY.dateTo}</span>
            <input
              type="date"
              data-testid="filter-end"
              aria-label={QA_COPY.to}
              value={filters.end ?? ''}
              min={filters.start || undefined}
              onChange={(e) => onChange({ end: e.target.value || undefined })}
              className={`${C.select} w-[150px]`}
            />
          </div>
        </div>
        <label className={C.label}>
          {QA_COPY.store}
          <select
            data-testid="filter-location"
            className={C.select}
            value={filters.location_id ?? ''}
            onChange={(e) => onChange({ location_id: e.target.value || undefined })}
          >
            <option value="">{QA_COPY.allStores}</option>
            {locations.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </select>
        </label>
        <label className={C.label}>
          {QA_COPY.language}
          <select
            data-testid="filter-lang"
            className={C.select}
            value={filters.lang ?? ''}
            onChange={(e) => onChange({ lang: (e.target.value || undefined) as LearnLang | undefined })}
          >
            <option value="">{QA_COPY.allLanguages}</option>
            <option value="en">{LANG_LABEL.en}</option>
            <option value="es">{LANG_LABEL.es}</option>
          </select>
        </label>
        <label className={C.label}>
          {QA_COPY.questionType}
          <select
            data-testid="filter-type"
            className={C.select}
            value={filters.question_type ?? ''}
            onChange={(e) => onChange({ question_type: (e.target.value || undefined) as QuestionType | undefined })}
          >
            <option value="">{QA_COPY.allTypes}</option>
            <option value="multiple_choice">{TYPE_LABEL.multiple_choice}</option>
            <option value="fill_blank">{TYPE_LABEL.fill_blank}</option>
          </select>
        </label>
        <label className={C.label}>
          {QA_COPY.lesson}
          <select
            data-testid="filter-drill"
            className={`${C.select} w-[190px]`}
            value={filters.drill_id ?? ''}
            onChange={(e) => onChange({ drill_id: e.target.value || undefined })}
          >
            <option value="">{QA_COPY.allLessons}</option>
            {drills.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        </label>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 pb-2">
          <label className="inline-flex items-center gap-2 text-sm font-semibold text-ink cursor-pointer">
            <input
              type="checkbox"
              data-testid="filter-flagged"
              className="w-4 h-4 rounded border-softBorder accent-coral"
              checked={!!filters.flagged_only}
              onChange={(e) => onChange({ flagged_only: e.target.checked || undefined })}
            />
            {QA_COPY.flaggedOnly}
          </label>
          {/* A real <label> for the switch: the whole phrase is the click target, and it is
              the switch's accessible name (a <button> is a labelable element). */}
          <label htmlFor={EARLIER_ID} className="inline-flex items-center gap-2 text-sm font-semibold text-ink cursor-pointer">
            <button
              type="button"
              role="switch"
              id={EARLIER_ID}
              data-testid="filter-earlier"
              aria-checked={on}
              onClick={() => onChange({ include_earlier_versions: on ? undefined : true })}
              className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-coral/40 ${
                on ? 'bg-coral' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                  on ? 'translate-x-4' : 'translate-x-0.5'
                }`}
              />
            </button>
            <span data-testid="filter-earlier-label">{QA_COPY.includeEarlier}</span>
          </label>
        </div>
        <button
          type="button"
          data-testid="filter-reset"
          onClick={onReset}
          className="ml-auto pb-2 text-sm font-semibold text-inkMuted hover:text-ink underline-offset-2 hover:underline"
        >
          {QA_COPY.resetFilters}
        </button>
      </div>
    </div>
  );
};

export default FilterBar;
